import React, { useState } from "react";
import styled from "styled-components";
import {
  Modal,
  Input,
  Button,
  Tag,
  Table,
  Space,
  Typography,
  Tooltip,
  message,
  Spin,
  Popconfirm,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { useSelector } from "react-redux";
import { getMenuList, getUserRoleId } from "redux/selectors";

const { Text } = Typography;

/* ─── Styled ─────────────────────────────────────────────── */
const TagInputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  border: 1px solid ${({ $hasError }) => ($hasError ? "#ff4d4f" : "#d9d9d9")};
  border-radius: 6px;
  padding: 4px 10px;
  min-height: 34px;
  background: #fff;
  cursor: text;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const AddRowWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 0 4px;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const NameLabel = styled.div`
  flex: 1;
  min-width: 0;
`;

const HoverActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transition: opacity 0.15s ease;
`;

const EditableLabel = styled(Text)`
  color: ${({ $editable }) =>
    $editable === "CUSTOM" ? "#2563eb" : "#1a1a1a"} !important;
  font-size: 13px;
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 11px;
  margin-top: 3px;
`;

/* ─── Component ──────────────────────────────────────────── */
const ManageCategoryTypeModal = ({
  open,
  onClose,
  typeList,
  setTypeList,
  getCategoryType,
  categoryListLoading,
}) => {
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);

  const Menulist =
    menuList[userRoleId] || Object.values(menuList)[0] || menuList;
  const [hoveredKey, setHoveredKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoadingKey, setDeleteLoadingKey] = useState(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [newTypeNames, setNewTypeNames] = useState([]);
  const [addError, setAddError] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  /* ── Row unique id ── */
  const getRowId = (record) => record.masterid;

  /* ── Edit ───────────────────────────────────────────────── */
  const handleEditStart = (item) => {
    setEditingKey(getRowId(item));
    setEditValue(item.categorytype);
  };

  const handleEditSave = async (record) => {
    const value = editValue.trim();

    if (!value) {
      message.error("Category type is required");
      return;
    }

    const duplicate = typeList?.some(
      (item) =>
        item.masterid !== record.masterid &&
        item.categorytype?.trim().toLowerCase() === value.toLowerCase()
    );

    if (duplicate) {
      message.error("This category type already exists");
      return;
    }

    try {
      setEditLoading(true);
      const { code } = await CALL_API(
        `/assets/managecategories/${record.masterid}`,
        "patch",
        {
          categorytype: editValue.trim(),
          usedcategories: record.usedcategories ?? [],
          totalassets: record.totalassets ?? 0,
          categorystatus: record.categorystatus,
        }
      );
      if (code === STATUS_CODE.SUCCESS) {
        message.success("Category type updated successfully");
        setEditingKey(null);
        setEditValue("");
        getCategoryType();
      } else {
        throw new Error(`Failed to update: ${code}`);
      }
    } catch (error) {
      message.error("Failed to update category type");
      console.error("Error updating category type:", error);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditingKey(null);
    setEditValue("");
  };

  /* ── Delete ─────────────────────────────────────────────── */
  const handleDelete = async (record) => {
    try {
      setDeleteLoadingKey(record.masterid);
      const { code } = await CALL_API(
        `/assets/managecategories/${record.masterid}`,
        "delete"
      );
      if (code === STATUS_CODE.SUCCESS) {
        message.success("Category type deleted successfully");
        getCategoryType();
      } else {
        throw new Error(`Failed to delete: ${code}`);
      }
    } catch (error) {
      message.error("Failed to delete category type");
      console.error("Error deleting category type:", error);
    } finally {
      setDeleteLoadingKey(null);
    }
  };

  /* ── Add ────────────────────────────────────────────────── */
  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();

      const value = tagInput.trim();

      const duplicate =
        newTypeNames.some(
          (item) => item.toLowerCase() === value.toLowerCase()
        ) ||
        typeList.some(
          (item) => item.categorytype?.toLowerCase() === value.toLowerCase()
        );

      if (duplicate) {
        setAddError("This category type already exists");
        return;
      }

      setNewTypeNames((prev) => [...prev, value]);
      setTagInput("");
      setAddError("");
    }
  };

  const handleAddType = async () => {
    // Include a name that was typed but not yet confirmed with Enter
    const pending = tagInput.trim();
    const typeNames =
      pending &&
      !newTypeNames.some((name) => name.toLowerCase() === pending.toLowerCase())
        ? [...newTypeNames, pending]
        : newTypeNames;

    if (!typeNames?.length) {
      setAddError("Please enter at least one category type");
      return;
    }

    const duplicate = typeNames.find((name) =>
      typeList?.some(
        (t) =>
          t.categorytype?.trim().toLowerCase() === name.trim().toLowerCase()
      )
    );

    if (duplicate) {
      setAddError(`"${duplicate}" already exists`);
      return;
    }

    try {
      setAddLoading(true);

      const payload = {
        categorytype: typeNames, // ["Laptop","Monitor","Printer"]
        usedcategories: [],
        totalassets: 0,
        categorystatus: "CUSTOM",
      };

      const { code } = await CALL_API(
        "/assets/managecategories",
        "post",
        payload
      );

      if (code === STATUS_CODE.SUCCESS) {
        message.success("Category types added successfully");

        setNewTypeNames([]);
        setTagInput("");
        setAddError("");
        setShowAddRow(false);

        getCategoryType();
      } else {
        message.error("Failed to add category type");
      }
    } catch (error) {
      message.error("Failed to add category type");
      console.error(error);
    } finally {
      setAddLoading(false);
    }
  };

  const handleCancelAdd = () => {
    setNewTypeNames([]);
    setTagInput("");
    setAddError("");
    setShowAddRow(false);
  };

  const handleModalClose = () => {
    handleCancelAdd();
    handleEditCancel();
    onClose();
  };

  /* ── Columns ────────────────────────────────────────────── */
  const columns = [
    {
      title: "Category Type",
      dataIndex: "categorytype",
      key: "categorytype",
      render: (name, record) => (
        <NameCell>
          {editingKey !== null && editingKey === getRowId(record) ? (
            /* inline edit mode */
            <Space size={6}>
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onPressEnter={() => handleEditSave(record)}
                size="small"
                style={{ width: 180, borderRadius: 4 }}
                autoFocus
              />
              <Tooltip title="Save">
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  style={{ background: "#2563eb" }}
                  loading={editLoading}
                  onClick={() => handleEditSave(record)}
                />
              </Tooltip>
              <Tooltip title="Cancel">
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleEditCancel}
                  disabled={editLoading}
                />
              </Tooltip>
            </Space>
          ) : (
            /* normal mode — buttons always in DOM, opacity controls visibility */
            <>
              <NameLabel>
                <EditableLabel $editable={record.categorystatus}>
                  {name}
                </EditableLabel>
              </NameLabel>
              <HoverActions
                $visible={
                  hoveredKey === getRowId(record) &&
                  record.categorystatus !== "DEFAULT"
                }
              >
                {Menulist?.find(
                  (item) =>
                    item?.key === "HRResources" &&
                    item?.subMenu?.find(
                      (subItem) =>
                        subItem?.key === "assets" && subItem?.permission?.edit
                    )
                ) && (
                  <Tooltip title="Edit">
                    <Button
                      type="primary"
                      size="small"
                      icon={<EditOutlined />}
                      style={{ background: "#2563eb", border: "none" }}
                      onClick={() => handleEditStart(record)}
                    />
                  </Tooltip>
                )}
                {Menulist?.find(
                  (item) =>
                    item?.key === "HRResources" &&
                    item?.subMenu?.find(
                      (subItem) =>
                        subItem?.key === "assets" && subItem?.permission?.delete
                    )
                ) && (
                  <Tooltip title="Delete">
                    <Popconfirm
                      title="Delete category type?"
                      description="This action cannot be undone."
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancel"
                      onConfirm={() => handleDelete(record)}
                    >
                      <Button
                        type="primary"
                        size="small"
                        icon={<DeleteOutlined />}
                        loading={deleteLoadingKey === record.masterid}
                      />
                    </Popconfirm>
                  </Tooltip>
                )}
              </HoverActions>
            </>
          )}
        </NameCell>
      ),
    },
    {
      title: "Used in Categories",
      dataIndex: "usedcategories",
      key: "usedcategories",
      render: (usedIn) =>
        usedIn?.length ? (
          <Text style={{ fontSize: 13, color: "#555" }}>
            {usedIn.join(", ")}
          </Text>
        ) : (
          <Text style={{ color: "#bbb" }}>---</Text>
        ),
    },
    {
      title: "Total Assets",
      dataIndex: "totalassets",
      key: "totalassets",
      align: "right",
      render: (v) => (
        <Text style={{ fontSize: 13, color: "#555" }}>{v ?? 0}</Text>
      ),
    },
  ];

  return (
    <Modal
      title="Category Type"
      open={open}
      onCancel={handleModalClose}
      footer={null}
      centered
      width={660}
    >
      <Spin spinning={categoryListLoading}>
        <Table
          columns={columns}
          dataSource={typeList}
          pagination={false}
          size="small"
          rowKey="masterid"
          onRow={(record) => ({
            onMouseEnter: () => setHoveredKey(getRowId(record)),
            onMouseLeave: () => setHoveredKey(null),
            // style: {
            //   background: hoveredKey === getRowId(record) ? "#fff" : "#f0f5ff",
            //   transition: "background 0.15s",
            // },
          })}
          style={{ border: "1px solid #f0f0f0", borderRadius: 8 }}
        />
      </Spin>

      {/* ── Add row ── */}
      {showAddRow ? (
        <AddRowWrapper>
          <div style={{ flex: 1 }}>
            <TagInputWrapper $hasError={!!addError}>
              {newTypeNames.map((item, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() =>
                    setNewTypeNames((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                  style={{ borderRadius: 4 }}
                >
                  {item}
                </Tag>
              ))}

              <input
                value={tagInput}
                onChange={(e) => {
                  setTagInput(e.target.value);
                  setAddError("");
                }}
                onKeyDown={handleTagKeyDown}
                placeholder="Type name and press Enter"
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: 13,
                  flexGrow: 1,
                  minWidth: 160,
                  background: "transparent",
                }}
              />
            </TagInputWrapper>
            {addError && <ErrorText>{addError}</ErrorText>}
          </div>

          <Space size={6} style={{ alignSelf: "flex-start" }}>
            <Button
              icon={<CloseOutlined />}
              onClick={handleCancelAdd}
              disabled={addLoading}
              style={{
                background: "#FCE3E3",
                borderColor: "#fca5a5",
                color: "#ef4444",
              }}
            >
              Cancel
            </Button>
            <Button
              icon={<CheckOutlined />}
              onClick={handleAddType}
              loading={addLoading}
              style={{
                background: "#E3E8FC",
                borderColor: "#93c5fd",
                color: "#2563eb",
              }}
            >
              Add
            </Button>
          </Space>
        </AddRowWrapper>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingTop: 12,
          }}
        >
          {Menulist?.find(
            (item) =>
              item?.key === "HRResources" &&
              item?.subMenu?.find(
                (subItem) =>
                  subItem?.key === "assets" && subItem?.permission?.create
              )
          ) && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => setShowAddRow(true)}
              style={{
                background: "#E3E8FC",
                border: "none",
                color: "#2D53DA",
              }}
            >
              Add Category Type
            </Button>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ManageCategoryTypeModal;
